import React, { useState, useMemo, useRef, useCallback } from "react";
import { IconButton, SvgIcon, LinearProgress } from "@mui/material";
import moment from "moment";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { mdiPlay, mdiPause } from "@mdi/js";
import SettingsMenu from "./SettingsMenu";
import { SkipNextBackward, SkipNextForward, Rewind, FastForward } from "orion-components/CBComponents/Icons";
import VideoDateRangeSelector from "./VideoDateRangeSelector";
import TimeLineControls from "./TimeLineControls";
import VideoScalePicker from "./VideoScalePicker";
import { useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { cameraService } from "client-app-core";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getWebrtcSettingByStreamAndValue } from "orion-components/GlobalData/Selectors";
import SpeedStack from "./SpeedStack";

let streamToBeRemoved = "";
let key = 0;

const RecordedBottomBar = ({
	cameraId,
	instanceId,
	playbackStreamName,
	isOnAlertProfile,
	alertDateTime,
	playerType,
	defaultToRecorded,
	playbackStartOffset,
	initialRecordedDuration,
	initialRecordedTimeUnit
}) => {
	const videoDateRangeSelSetStartDateExternallyRef = useRef(null);
	const [videoLoadProgress, setVideoLoadProgress] = useState(0);
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const [duration, setDuration] = useState();
	const [durationSet, setDurationSet] = useState(false);
	const [scale, setScale] = useState(null);
	const [currentTime, setCurrentTime] = useState(null);
	const [startTime, setStartTime] = useState(null);
	const [startDateTime, setStartDateTime] = useState(null);
	const [endDateTime, setEndDateTime] = useState(null);
	const [speed, setSpeed] = useState(1);
	const [isMouseOverChevronLeft, setIsMouseOverChevronLeft] = useState(false);
	const [isMouseOverChevronRight, setIsMouseOverChevronRight] = useState(false);
	const [componentKey, setComponentKey] = useState(0);
	const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
	const [supportedSpeeds, setSupportedSpeeds] = useState([-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16]);

	const dir = useSelector(state => getDir(state));

	const containerRef = useRef(null);
	const timeLineRef = useRef(null);
	const videoBottomBarRef = useRef(null);

	/**
	 * Right now recordedMode is always "recorded", but we will maintain the ability to
	 * switch to "clip" mode in the furture
	 */
	const recordedMode = "recorded";

	useEffect(() => {
		EventBus.subscribe("getStreamToBeRemoved", getStreamToBeRemoved);

		EventBus.subscribe("RecordedBottomBar_startVideo", startVideo);
		EventBus.subscribe("RecordedBottomBar_loadProgress", setLoadProgress);

		EventBus.subscribe("NewVideoPlayer_VideoDurationPicker_SetVideoDurationExternally", setVideoDurationExternally);

		EventBus.subscribe("VideoPlayer_CapabilitiesReady", handlePlayerCapabilitiesReady);

		return () => {
			EventBus.unsubscribe("getStreamToBeRemoved", getStreamToBeRemoved);

			EventBus.unsubscribe("RecordedBottomBar_startVideo", startVideo);
			EventBus.unsubscribe("RecordedBottomBar_loadProgress", setLoadProgress);

			EventBus.unsubscribe(
				"NewVideoPlayer_VideoDurationPicker_SetVideoDurationExternally",
				setVideoDurationExternally
			);

			EventBus.unsubscribe("VideoPlayer_CapabilitiesReady", handlePlayerCapabilitiesReady);
		};
	}, []);

	useEffect(() => {
		if (isOnAlertProfile) {
			// should always be seconds for alert profile but just in case
			const multiplier =
				initialRecordedTimeUnit === "seconds" ? 1 : initialRecordedTimeUnit === "minutes" ? 60 : 3600;
			const durationSeconds = isNaN(initialRecordedDuration)
				? 20 // default 20 seconds
				: parseInt(initialRecordedDuration, 10) * multiplier; // configured in seconds
			const newDuration = durationSeconds / 60; // convert to minutes
			setDuration(newDuration);
		} else {
			setDuration(10); // default 10 minutes
		}
		setDurationSet(true);
	}, [isOnAlertProfile]);

	const toggleSettingsMenu = value => {
		setIsSettingsMenuOpen(value);
	};

	const getDuration = value => {
		setDuration(value);
	};

	const getScale = value => {
		setScale(value);
	};

	const handlePlayerCapabilitiesReady = data => {
		if (data.instanceId !== instanceId) {
			return;
		}
		if (data.supportedSpeeds) {
			console.debug("[RecordedBottomBar] Received supported speeds from player:", data.supportedSpeeds);
			setSupportedSpeeds(data.supportedSpeeds);
		} else {
			console.debug("[RecordedBottomBar] Received capabilities without supported speeds, using defaults");
			setSupportedSpeeds([-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16]);
		}
	};

	const setVideoDurationExternally = data => {
		if (data.instanceId !== instanceId) {
			return;
		}

		getDuration(data.duration);
		setSpeed(1); // reset speed to 1x

		switch (playerType) {
			case "avigilon":
				EventBus.publish("AvigilonRecordedWebRTCPlayerWrapper_renderNewComponentInstance", { instanceId });
				break;
			case "vicads":
				EventBus.publish("VicadsRecordedWebRTCPlayerWrapper_renderNewComponentInstance", { instanceId });
				break;
			case "flashphoner":
				EventBus.publish("PlaybackRecordedWebRTCPlayerWrapper_renderNewComponentInstance");
				break;
			default:
				console.error("Unknown camera system");
				return;
		}
	};

	const getStartTime = value => {
		setStartTime(value);
		setCurrentTime(null);
		key++;
		setComponentKey(key);
	};

	const getStartAndEndDateTime = (start, end) => {
		setStartDateTime(start);
		setEndDateTime(end);
	};

	const getStreamToBeRemoved = name => {
		streamToBeRemoved = name;
	};

	useEffect(() => {
		if (defaultToRecorded && !isOnAlertProfile) {
			const parsedDate = new Date();
			handleSetStartDateExternally({
				instanceId,
				userSelectedDateTime: parsedDate.toString(),
				durationVal: initialRecordedDuration,
				durationType: initialRecordedTimeUnit
			});
			let finalDuration = initialRecordedDuration;
			if (initialRecordedTimeUnit === "seconds") {
				finalDuration = finalDuration / 60;
			}
			if (initialRecordedTimeUnit === "hours") {
				finalDuration = finalDuration * 60;
			}
			EventBus.publish("NewVideoPlayer_VideoDurationPicker_SetVideoDurationExternally", {
				instanceId,
				duration: finalDuration
			});
		}
	}, [defaultToRecorded, initialRecordedTimeUnit, initialRecordedDuration, isOnAlertProfile]);

	useEffect(() => {
		return () => {
			if (streamToBeRemoved !== "") {
				cameraService.removeWowzaStream(cameraId, true, err => {
					if (err) {
						console.log("ERROR", err);
						return;
					}
				});
			}
		};
	}, []);

	const styles = useMemo(() => {
		return {
			container: {
				display: "flex",
				height: "100%",
				width: "100%",
				alignItems: "center"
			},
			leftChevron: {
				height: 32,
				width: 14,
				background: "#000000"
			},
			chevronContainer: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: "100%",
				height: "100%"
			},
			chevronWrapper: {
				padding: 0,
				height: 5.1,
				width: 3
			},
			playbackControls: {
				position: "absolute",
				bottom: 0,
				display: "flex",
				height: "100%",
				justifyContent: "space-between",
				alignItems: "center",
				background: "#000",
				zIndex: 2,
				...(dir === "ltr" && { left: 14 }),
				...(dir === "rtl" && { right: 14 })
			},
			rightChevron: {
				height: 32,
				width: 14,
				background: "#000000"
			},
			timeLineWrapper: {
				width: "100%",
				height: "100%",
				overflowX: "hidden"
			},
			settingsSection: {
				position: "absolute",
				bottom: 0,
				width: "auto",
				height: "100%",
				display: "flex",
				alignItems: "center",
				background: "#000",
				zIndex: 2,
				...(dir === "ltr" && { right: 14, paddingRight: 6 }),
				...(dir === "rtl" && { left: 14, paddingLeft: 6 })
			}
		};
	}, [dir]);

	const connected = useSelector(state => getWebrtcSettingByStreamAndValue(state, playbackStreamName, "connected"));

	useEffect(() => {
		if (connected) {
			setIsVideoPlaying(true);
		}
	}, [connected]);

	const startVideo = data => {
		if (typeof data === "boolean") {
			setIsVideoPlaying(data);
		} else {
			if (data.instanceId === instanceId) {
				setIsVideoPlaying(data.playing);
			}
		}
	};

	const setLoadProgress = data => {
		if (data.instanceId === instanceId) {
			setVideoLoadProgress(data.progress);
		}
	};

	const moveForward = byScale => {
		// byScale = true, move forward one scale, else move forward one frame (scale/10)
		if (isVideoPlaying || currentTime >= endDateTime) {
			return;
		}

		if (playerType === "native") {
			// Native player supports stepping exactly on frame forward or backward
			EventBus.publish("Universal_PlaybackCommand", {
				instanceId,
				command: "stepForward"
			});
			return;
		}

		let newTime = moment(currentTime).add(byScale ? scale : scale / 10, "m");
		if (newTime > endDateTime) {
			newTime = endDateTime;
		}

		EventBus.publish("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setCursor",
			time: newTime.toDate()
		});

		EventBus.publish("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setCursor",
			time: newTime.toDate()
		});

		EventBus.publish("Universal_PlaybackCommand", {
			instanceId,
			seeking: true,
			command: "setCursor",
			time: newTime.toDate()
		});
	};

	const moveBackward = byScale => {
		// byScale = true, move backward one scale, else move backward one frame (scale/10)
		if (isVideoPlaying || currentTime <= startDateTime) {
			// We allow operation only in paused mode
			return;
		}

		if (playerType === "native") {
			// Native player supports stepping exactly on frame forward or backward
			EventBus.publish("Universal_PlaybackCommand", {
				instanceId,
				command: "stepBackward"
			});
			return;
		}

		let newTime = moment(currentTime).subtract(byScale ? scale : scale / 10, "m");
		if (newTime < startDateTime) {
			newTime = startDateTime;
		}

		EventBus.publish("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setCursor",
			time: newTime.toDate()
		});

		EventBus.publish("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setCursor",
			time: newTime.toDate()
		});
		EventBus.publish("Universal_PlaybackCommand", {
			instanceId,
			seeking: true,
			command: "setCursor",
			time: newTime.toDate()
		});
	};

	const increaseSpeed = () => {
		if (!isVideoPlaying) {
			// We allow operation only in play mode
			return;
		}

		// Find current speed in supported speeds array
		const currentIndex = supportedSpeeds.indexOf(speed);
		if (currentIndex === -1 || currentIndex === supportedSpeeds.length - 1) {
			// Speed not found or already at max
			return;
		}

		const newSpeed = supportedSpeeds[currentIndex + 1];

		EventBus.publish("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setSpeed",
			speed: newSpeed
		});

		EventBus.publish("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setSpeed",
			speed: newSpeed
		});
		EventBus.publish("Universal_PlaybackCommand", {
			instanceId,
			command: "setSpeed",
			speed: newSpeed
		});
		setSpeed(newSpeed);
	};

	const decreaseSpeed = () => {
		if (!isVideoPlaying) {
			// We allow operation only in play mode
			return;
		}

		// Find current speed in supported speeds array
		const currentIndex = supportedSpeeds.indexOf(speed);
		if (currentIndex === -1 || currentIndex === 0) {
			// Speed not found or already at min
			return;
		}

		const newSpeed = supportedSpeeds[currentIndex - 1];

		EventBus.publish("AvigilonRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setSpeed",
			speed: newSpeed
		});

		EventBus.publish("VicadsRecordedWebRTCPlayer_applyPlaybackCommand", {
			instanceId,
			command: "setSpeed",
			speed: newSpeed
		});
		EventBus.publish("Universal_PlaybackCommand", {
			instanceId,
			command: "setSpeed",
			speed: newSpeed
		});

		setSpeed(newSpeed);
	};

	const handleSetStartDateExternally = useCallback(payload => {
		if (videoDateRangeSelSetStartDateExternallyRef.current) {
			videoDateRangeSelSetStartDateExternallyRef.current(payload);
		}
	}, []);
	return (
		<div
			className="bccVideoBottomBarWrapper bccRecordedBottomBarWrapper"
			style={{ zIndex: 1901 }}
			ref={videoBottomBarRef}
		>
			<div ref={containerRef} style={styles.container}>
				{recordedMode === "recorded" && (
					<div style={styles.leftChevron}>
						<div
							style={{
								...styles.chevronContainer,
								visibility: `${
									isVideoPlaying ||
									(dir === "ltr" && currentTime?.getTime() === startDateTime?.valueOf()) ||
									(dir === "rtl" && currentTime?.getTime() === endDateTime?.valueOf())
										? "hidden"
										: "visible"
								}`,
								background: !isMouseOverChevronLeft ? "#000000" : "#C9C9C9"
							}}
							onMouseEnter={() => {
								setIsMouseOverChevronLeft(!isMouseOverChevronLeft);
							}}
							onMouseLeave={() => {
								setIsMouseOverChevronLeft(!isMouseOverChevronLeft);
							}}
						>
							<IconButton
								sx={{ ...styles.chevronWrapper, color: !isMouseOverChevronLeft ? "#fff" : "#000000" }}
								onClick={() => (dir === "ltr" ? moveBackward(true) : moveForward(true))}
							>
								{dir === "ltr" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
							</IconButton>
						</div>
					</div>
				)}

				<div style={styles.timeLineWrapper}>
					{startTime !== null && durationSet && (
						<TimeLineControls
							key={componentKey}
							instanceId={instanceId}
							recordedMode={recordedMode}
							videoDurationInMinutes={duration}
							videoScaleInMinutes={scale}
							startTime={startTime}
							currentTime={currentTime}
							isPlaying={isVideoPlaying}
							cameraId={cameraId}
							setCurrentTime={setCurrentTime}
						/>
					)}
				</div>
				{recordedMode === "recorded" && (
					<div style={styles.rightChevron}>
						<div
							style={{
								...styles.chevronContainer,
								visibility: `${
									isVideoPlaying ||
									(dir === "ltr" && currentTime?.getTime() === endDateTime?.valueOf()) ||
									(dir === "rtl" && currentTime?.getTime() === startDateTime?.valueOf())
										? "hidden"
										: "visible"
								}`,
								background: !isMouseOverChevronRight ? "#000000" : "#C9C9C9"
							}}
							onMouseEnter={() => {
								setIsMouseOverChevronRight(!isMouseOverChevronRight);
							}}
							onMouseLeave={() => {
								setIsMouseOverChevronRight(!isMouseOverChevronRight);
							}}
						>
							<IconButton
								sx={{ ...styles.chevronWrapper, color: !isMouseOverChevronRight ? "#fff" : "#000000" }}
								onClick={() => (dir === "ltr" ? moveForward(true) : moveBackward(true))}
							>
								{dir === "ltr" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
							</IconButton>
						</div>
					</div>
				)}

				{recordedMode === "recorded" && (
					<div ref={timeLineRef} style={styles.playbackControls}>
						<div style={{ float: dir === "ltr" ? "left" : "right", width: "auto" }}>
							<div
								style={{
									display: "flex",
									...(!isVideoPlaying && { alignItems: "center" }),
									width: "100%",
									height: "100%"
								}}
							>
								<div
									style={{
										marginInlineEnd: 8.7
									}}
									onClick={() => {
										if (isVideoPlaying) {
											if (dir === "ltr") {
												decreaseSpeed();
											} else {
												increaseSpeed();
											}
										} else {
											if (dir === "ltr") {
												moveBackward(false);
											} else {
												moveForward(false);
											}
										}
									}}
								>
									{!isVideoPlaying ? (
										dir === "ltr" ? (
											<div>
												<SkipNextBackward fillColor="#fff" />
											</div>
										) : (
											<div>
												<SkipNextForward fillColor="#fff" />
											</div>
										)
									) : dir === "ltr" ? (
										<SpeedStack
											show={speed < 0}
											text={`${speed}x`}
											icon={
												<Rewind fillColor="#fff" customStyles={{ transform: "scale(1.1)" }} />
											}
										/>
									) : (
										<SpeedStack
											show={speed > 0}
											text={`+${speed}x`}
											icon={
												<FastForward
													fillColor="#fff"
													customStyles={{ transform: "scale(1.1)" }}
												/>
											}
										/>
									)}
								</div>
								<div>
									<IconButton
										sx={{ zIndex: 2 }}
										onClick={() => {
											setIsVideoPlaying(!isVideoPlaying);
											if (isVideoPlaying) {
												switch (playerType) {
													case "avigilon":
														EventBus.publish(
															"AvigilonRecordedWebRTCPlayer_applyPlaybackCommand",
															{ instanceId, command: "pause" }
														);
														break;
													case "vicads":
														EventBus.publish(
															"VicadsRecordedWebRTCPlayer_applyPlaybackCommand",
															{ instanceId, command: "pause" }
														);
														break;
													case "flashphoner":
														EventBus.publish("Playback_stopVideoStreaming");
														break;
													case "native":
													case "ws-jpeg":
														EventBus.publish("Universal_PlaybackCommand", {
															instanceId,
															command: "pause"
														});
														break;
													default:
														return;
												}
											} else {
												switch (playerType) {
													case "avigilon":
														EventBus.publish(
															"AvigilonRecordedWebRTCPlayer_applyPlaybackCommand",
															{ instanceId, command: "play" }
														);
														break;
													case "vicads":
														EventBus.publish(
															"VicadsRecordedWebRTCPlayer_applyPlaybackCommand",
															{ instanceId, command: "play" }
														);
														break;
													case "flashphoner":
														EventBus.publish("Playback_restartstopVideoStreaming");
														break;
													case "native":
													case "ws-jpeg":
														EventBus.publish("Universal_PlaybackCommand", {
															instanceId,
															command: "play",
															speed
														});
														break;
													default:
														return;
												}
											}
										}}
									>
										<div
											className="bcc-playBack-play-pause-button"
											style={{
												backgroundColor: !isVideoPlaying ? "#4DB5F4" : "#BABABA",
												display: "flex",
												alignItems: "center",
												justifyContent: "center"
											}}
										>
											<SvgIcon
												style={{
													width: 10,
													height: 9.93,
													color: "#FFFFFF",
													transform: "scale(2)"
												}}
											>
												<path d={!isVideoPlaying ? mdiPlay : mdiPause} />
											</SvgIcon>
										</div>
									</IconButton>
								</div>

								<div
									style={{
										marginInlineStart: 7.31
									}}
									onClick={() => {
										if (isVideoPlaying) {
											if (dir === "ltr") {
												increaseSpeed();
											} else {
												decreaseSpeed();
											}
										} else {
											if (dir === "ltr") {
												moveForward(false);
											} else {
												moveBackward(false);
											}
										}
									}}
								>
									{!isVideoPlaying ? (
										dir === "ltr" ? (
											<div>
												<SkipNextForward fillColor="#fff" />
											</div>
										) : (
											<div>
												<SkipNextBackward fillColor="#fff" />
											</div>
										)
									) : dir === "ltr" ? (
										<SpeedStack
											show={speed > 0}
											text={`+${speed}x`}
											icon={
												<FastForward
													fillColor="#fff"
													customStyles={{ transform: "scale(1.1)" }}
												/>
											}
										/>
									) : (
										<SpeedStack
											show={speed < 0}
											text={`${speed}x`}
											icon={
												<Rewind fillColor="#fff" customStyles={{ transform: "scale(1.1)" }} />
											}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				)}

				<div style={styles.settingsSection}>
					<div
						style={{
							marginLeft: 8,
							width: "auto",
							marginRight: 8,
							zIndex: !isSettingsMenuOpen ? 2 : 1
						}}
					>
						<VideoScalePicker duration={duration} getScale={getScale} />
					</div>
					{recordedMode === "recorded" && (
						<SettingsMenu
							instanceId={instanceId}
							toggleSettingsMenu={toggleSettingsMenu}
							setVideoDurationExternally={setVideoDurationExternally}
							setStartDateExternally={handleSetStartDateExternally}
						/>
					)}
				</div>
			</div>
			{recordedMode === "recorded" &&
				(playerType === "vicads" || playerType === "avigilon") &&
				videoLoadProgress < 100 && (
					<div style={{ position: "absolute", bottom: 32, width: "100%", height: 5 }}>
						<LinearProgress
							dir={dir}
							style={{ height: 5, width: "100%" }}
							variant="determinate"
							value={videoLoadProgress}
						/>
					</div>
				)}
			{durationSet && (
				<VideoDateRangeSelector
					instanceId={instanceId}
					duration={duration}
					getStartTime={getStartTime}
					isOnAlertProfile={isOnAlertProfile}
					alertDateTime={alertDateTime}
					getStartAndEndDateTime={getStartAndEndDateTime}
					getDuration={getDuration}
					playbackStartOffset={playbackStartOffset}
					playerType={playerType}
					recordedMode={recordedMode}
					initialRecordedDuration={initialRecordedDuration}
					initialRecordedTimeUnit={initialRecordedTimeUnit}
					setVideoDurationExternally={setVideoDurationExternally}
					registerSetStartDateExternally={fn => (videoDateRangeSelSetStartDateExternallyRef.current = fn)}
				/>
			)}
		</div>
	);
};

RecordedBottomBar.propTypes = {
	cameraId: PropTypes.string,
	instanceId: PropTypes.string,
	isOnAlertProfile: PropTypes.bool,
	alertDateTime: PropTypes.any,
	defaultToRecorded: PropTypes.bool,
	initialRecordedDuration: PropTypes.string,
	initialRecordedTimeUnit: PropTypes.string,
	playbackStreamName: PropTypes.string,
	playerType: PropTypes.string
};

export default RecordedBottomBar;
